/**
 * The flavour wheel figure on its own: the flavour database wheel for up to
 * three ingredients, in All notes or Key notes mode.
 *
 * Presentational only. It fetches nothing and reads no app context, so the
 * wheel screen draws it full size and the development chat can draw one
 * inside a reply (`compact`). Every number comes from `wheelFigure` in
 * `@baros/core`'s wheel.ts; this file turns the figure into SVG and reports
 * taps on a note.
 *
 * Colours are the families' and the ingredients' own, which read on either
 * theme; the rings, hub and text around them are theme variables.
 */

import { useId } from "react";
import {
  FLAVOUR_FAMILIES,
  FLAVOUR_NOTES,
  noteRadius,
  sectorPath,
  wheelFigure,
  wheelPoint,
  WHEEL_CENTRE,
  WHEEL_RADII,
  WHEEL_SIZE,
  type FamilyArc,
  type FlavourProfile,
  type WheelMode,
} from "@baros/core";
import "./flavour-wheel.css";

export interface FlavourWheelItem {
  /** Stable across renders: an ingredient id, or a flavour-database name. */
  readonly key: string;
  readonly label: string;
  readonly colour: string;
  readonly profile: Pick<FlavourProfile, "notes">;
}

export interface FlavourWheelProps {
  /** Up to three; any more are ignored, as the reference wheel ignores them. */
  readonly items: readonly FlavourWheelItem[];
  readonly mode: WheelMode;
  /** The note whose card is open, highlighted on the wheel. */
  readonly selectedNote?: number | null | undefined;
  /** Called with a note index (0–139) when a note is tapped. Without it, notes are not interactive. */
  readonly onNote?: ((index: number) => void) | undefined;
  /**
   * For small sizes, such as inside a chat reply: the grey labels of All
   * notes mode are dropped (at that size they are specks) and the family
   * names go with them.
   */
  readonly compact?: boolean | undefined;
  readonly className?: string | undefined;
}

/** Where the family names run, inside the coloured ring. */
const FAMILY_LABEL_RADIUS = 92;
const FAMILY_LABEL_SIZE = 8.5;
/** The hit area runs past the label radius so a label is tappable too. */
const HIT_OUTER = WHEEL_RADII.label + 70;

const FAMILY_NAMES: ReadonlyMap<string, string> = new Map(FLAVOUR_FAMILIES.map((f) => [f.key, f.key.toUpperCase()]));

/**
 * An arc for a family name to run along. In the lower half the arc runs
 * anticlockwise and a little further out, so the name reads left to right
 * rather than upside down.
 */
function familyLabelArc(arc: FamilyArc): string | null {
  const mid = (arc.start + arc.end) / 2;
  const name = FAMILY_NAMES.get(arc.family) ?? "";
  // Too narrow to hold its name (Key notes can shrink a family to a sliver).
  if ((arc.end - arc.start) * FAMILY_LABEL_RADIUS < name.length * FAMILY_LABEL_SIZE * 0.78) return null;
  const lower = mid > Math.PI / 2 && mid < (3 * Math.PI) / 2;
  const r = lower ? FAMILY_LABEL_RADIUS + FAMILY_LABEL_SIZE * 0.7 : FAMILY_LABEL_RADIUS;
  const [x0, y0] = wheelPoint(r, lower ? arc.end : arc.start);
  const [x1, y1] = wheelPoint(r, lower ? arc.start : arc.end);
  const large = arc.end - arc.start > Math.PI ? 1 : 0;
  return `M${x0.toFixed(2)},${y0.toFixed(2)} A${r},${r} 0 ${large} ${lower ? 0 : 1} ${x1.toFixed(2)},${y1.toFixed(2)}`;
}

export function FlavourWheel({ items, mode, selectedNote, onNote, compact, className }: FlavourWheelProps) {
  const uid = useId().replace(/:/g, "");
  const shown = items.slice(0, 3);
  // Cheap enough (140 notes) to recompute on every render, which spares the
  // callers from keeping `items` referentially stable.
  const figure = wheelFigure(shown.map((i) => ({ profile: i.profile, colour: i.colour })), mode);
  const selected = typeof selectedNote === "number" ? figure.notes.find((n) => n.index === selectedNote) : undefined;
  const names = shown.map((i) => i.label).join(", ");

  return (
    <svg
      className={["flavour-wheel", `mode-${mode}`, compact ? "compact" : "", className ?? ""].filter(Boolean).join(" ")}
      viewBox={`0 0 ${WHEEL_SIZE} ${WHEEL_SIZE}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={
        shown.length
          ? `Flavour wheel for ${names}: ${figure.count} ${mode === "key" ? "key" : "active"} notes`
          : "Flavour wheel"
      }
    >
      {/* the highlight behind the selected note */}
      {selected && (
        <path className="fw-selected" d={sectorPath(WHEEL_RADII.familyOuter, HIT_OUTER - 6, selected.start, selected.end)} />
      )}

      {/* guide rings at 25, 50, 75 and 100% */}
      {WHEEL_RADII.rings.map((r) => (
        <circle key={r} className="fw-ring" cx={WHEEL_CENTRE} cy={WHEEL_CENTRE} r={r} />
      ))}

      {/* the family ring and its names */}
      {figure.arcs.map((arc) => (
        <path
          key={arc.family}
          d={sectorPath(WHEEL_RADII.familyInner, WHEEL_RADII.familyOuter, arc.start, arc.end)}
          fill={arc.colour}
        >
          <title>{`${arc.family} · ${arc.slots.length} notes`}</title>
        </path>
      ))}
      {!compact &&
        figure.arcs.map((arc) => {
          const d = familyLabelArc(arc);
          if (!d) return null;
          const id = `fw-${uid}-${arc.family}`;
          return (
            <g key={id} className="fw-family">
              <path id={id} d={d} fill="none" stroke="none" />
              <text fontSize={FAMILY_LABEL_SIZE} fill={arc.colour}>
                <textPath href={`#${id}`} startOffset="50%" textAnchor="middle">
                  {FAMILY_NAMES.get(arc.family)}
                </textPath>
              </text>
            </g>
          );
        })}

      {/* All notes: one unfilled radar line per ingredient */}
      {figure.radar.map((r) => (
        <path key={r.ingredient} className="radarLine" d={r.d} stroke={r.colour} />
      ))}

      {/* Key notes: a bar per qualifying ingredient, a leader to each label */}
      {figure.bars.map((b) => (
        <path key={`${b.index}:${b.ingredient}`} className="fw-bar" d={b.d} fill={b.colour}>
          <title>{`${shown[b.ingredient]?.label ?? ""} · ${FLAVOUR_NOTES[b.index]!.label} ${b.value}`}</title>
        </path>
      ))}
      {mode === "key" &&
        figure.notes.map((n) => {
          const top = Math.max(...shown.map((i) => noteRadius(i.profile.notes[n.index] ?? 0)));
          const [x0, y0] = wheelPoint(top + 3, n.angle);
          const [x1, y1] = wheelPoint(WHEEL_RADII.label - 4, n.labelAngle);
          return <line key={n.index} className="fw-leader" x1={x0} y1={y0} x2={x1} y2={y1} stroke={n.colour} />;
        })}

      {/* labels: grey first so the bold ones sit on top */}
      {[...figure.notes]
        .filter((n) => n.active || !compact)
        .sort((a, b) => Number(a.active) - Number(b.active))
        .map((n) => (
          <text
            key={n.index}
            className={n.active ? "fw-label on" : "fw-label"}
            x={n.placement.x}
            y={n.placement.y}
            transform={`rotate(${n.placement.rotate} ${n.placement.x} ${n.placement.y})`}
            textAnchor={n.placement.anchor}
            dominantBaseline="central"
            fontSize={n.size}
            fontWeight={n.weight}
            // As a style, not the `fill` attribute: the stylesheet's grey for
            // `.fw-label` outranks an attribute, and every bold label came out grey.
            style={n.active ? { fill: n.colour } : undefined}
            opacity={n.opacity}
            aria-current={n.index === selectedNote ? "true" : undefined}
          >
            {n.label}
          </text>
        ))}

      {/* the hub */}
      <circle className="fw-hub" cx={WHEEL_CENTRE} cy={WHEEL_CENTRE} r={WHEEL_RADII.hub} />
      <text className="fw-count" x={WHEEL_CENTRE} y={WHEEL_CENTRE - 4} textAnchor="middle" dominantBaseline="central">
        {figure.count}
      </text>
      <text className="fw-count-sub" x={WHEEL_CENTRE} y={WHEEL_CENTRE + 20} textAnchor="middle" dominantBaseline="central">
        {mode === "key" ? "KEY NOTES" : "NOTES"}
      </text>

      {/* invisible hit areas, one per shown note */}
      {onNote &&
        figure.notes.map((n) => (
          <path
            key={n.index}
            className="noteHit"
            d={sectorPath(WHEEL_RADII.hub + 2, HIT_OUTER, n.start, n.end)}
            role="button"
            tabIndex={compact ? -1 : 0}
            aria-label={`${n.label}, ${n.family}`}
            aria-pressed={n.index === selectedNote}
            onClick={() => onNote(n.index)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onNote(n.index);
              }
            }}
          >
            <title>{n.label}</title>
          </path>
        ))}
    </svg>
  );
}
